/**
 * Proactive "Stripe needs your attention" panel that admins see on the
 * Connected Apps page when a Connect account is partially set up.
 *
 * It is a *summary first*: one compact bar naming the problem, the deadline,
 * and the single button that fixes it. The itemised list is real information —
 * an admin needs to know it's the bank account and not the ToS — but a
 * six-item account was expanding it into a full screen of amber and pushing
 * every integration card below the fold. So the list is a disclosure, and when
 * opened it lays the categories out in columns rather than one tall stack.
 *
 * The Stripe `requirements.*` payload from the BE arrives already humanised
 * (see stripe-requirements.mapper.ts on the operations side) — this component
 * just lays it out by category with a single CTA that deep-links into the
 * embedded Stripe management panel.
 *
 * Render contract:
 *   • If `requirements` is null / undefined or there are zero
 *     `pastDue` + `currentlyDue` items, render nothing.
 *   • `pendingVerification` items are shown as a footnote (Stripe is
 *     actively reviewing them; the admin can't do anything to speed it
 *     up) and don't count toward the "needs attention" gate.
 */
import { useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  ChevronDown,
  ClipboardCheck,
  Clock,
  IdCard,
  Landmark,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { cn } from '@/lib/utils';
import type {
  StripeRequirementCategory,
  StripeRequirementSummary,
  StripeRequirementsInfo,
} from '@/types/settings';

interface StripeRequirementsCardProps {
  requirements: StripeRequirementsInfo | null | undefined;
  /** Callback wired to the "Resolve in Stripe" button. */
  onResolve: () => void;
  className?: string;
}

interface GroupDescriptor {
  category: StripeRequirementCategory;
  label: string;
  icon: ReactNode;
}

/**
 * Display order is intentional: Identity first (most common gating
 * step), Business next, Bank, then Terms. Anything we couldn't classify
 * shows last under "Other" so the admin still sees it.
 */
const GROUPS: GroupDescriptor[] = [
  { category: 'individual', label: 'Identity', icon: <IdCard className="size-3.5" /> },
  { category: 'business', label: 'Business', icon: <Building2 className="size-3.5" /> },
  { category: 'bank', label: 'Bank', icon: <Landmark className="size-3.5" /> },
  { category: 'tos', label: 'Terms', icon: <ClipboardCheck className="size-3.5" /> },
  { category: 'other', label: 'Other', icon: <AlertTriangle className="size-3.5" /> },
];

function hasActionableItems(requirements: StripeRequirementsInfo): boolean {
  return requirements.pastDue.length + requirements.currentlyDue.length > 0;
}

function groupByCategory(
  items: StripeRequirementSummary[],
): Record<StripeRequirementCategory, StripeRequirementSummary[]> {
  const grouped: Record<StripeRequirementCategory, StripeRequirementSummary[]> = {
    individual: [],
    business: [],
    tos: [],
    bank: [],
    other: [],
  };
  for (const item of items) {
    grouped[item.category].push(item);
  }
  return grouped;
}

/**
 * De-dupes a requirement code that's in both pastDue and currentlyDue —
 * Stripe sometimes lists the same code in both arrays during the grace
 * window. We surface the past-due copy because that's the more urgent
 * label for the UI.
 */
function uniqueByCode(items: StripeRequirementSummary[]): StripeRequirementSummary[] {
  const seen = new Set<string>();
  const out: StripeRequirementSummary[] = [];
  for (const item of items) {
    if (seen.has(item.code)) continue;
    seen.add(item.code);
    out.push(item);
  }
  return out;
}

function formatDeadline(iso: string | null, timeZone?: string | null): string | null {
  if (!iso) return null;
  const formatted = formatDateTimeInTimeZone(iso, timeZone, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return formatted || null;
}

export function StripeRequirementsCard({
  requirements,
  onResolve,
  className,
}: StripeRequirementsCardProps) {
  const timeZone = useCompanyTimezone();
  const [open, setOpen] = useState(false);

  if (!requirements || !hasActionableItems(requirements)) {
    return null;
  }

  // Merge past-due + currently-due into one display list (past-due wins
  // on dupes) so the admin doesn't see the same requirement twice.
  const merged = uniqueByCode([...requirements.pastDue, ...requirements.currentlyDue]);
  const grouped = groupByCategory(merged);
  const pastDueCodes = new Set(requirements.pastDue.map((item) => item.code));

  const deadline = formatDeadline(requirements.currentDeadline, timeZone);
  const totalCount = merged.length;

  return (
    <section
      data-testid="stripe-requirements-card"
      className={cn(
        'rounded-xl border border-amber-300/80 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-800/60 dark:bg-amber-950/25 dark:text-amber-100',
        className,
      )}
      aria-label="Stripe needs your attention"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <AlertTriangle className="size-4 shrink-0" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-5">Stripe needs your attention</p>
          <p className="text-xs leading-5 text-amber-900/80 dark:text-amber-100/80">
            {totalCount === 1
              ? '1 requirement is blocking payouts on this Connect account.'
              : `${totalCount} requirements are blocking payouts on this Connect account.`}
            {deadline ? ` Required by ${deadline}.` : null}
          </p>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          className="shrink-0 text-amber-900 hover:bg-amber-100 dark:text-amber-100 dark:hover:bg-amber-900/40"
        >
          {open ? 'Hide' : 'View'} {totalCount === 1 ? 'requirement' : 'requirements'}
          <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
        </Button>
        <Button
          size="sm"
          variant="default"
          onClick={onResolve}
          className="shrink-0 bg-amber-600 text-white hover:bg-amber-700 focus-visible:ring-amber-500"
        >
          Resolve in Stripe
          <ArrowRight className="size-3.5" />
        </Button>
      </div>

      {open ? (
        <div className="mt-3 border-t border-amber-200/80 pt-3 dark:border-amber-800/50">
          {/* Columns, not one tall stack — a six-item account was otherwise
              taller than the entire card grid it sits above. */}
          <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">
            {GROUPS.map((group) => {
              const items = grouped[group.category];
              if (items.length === 0) return null;
              return (
                <li key={group.category} className="min-w-0">
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-amber-900/70 dark:text-amber-100/70">
                    {group.icon}
                    <span>{group.label}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {items.map((item) => (
                      <li key={item.code} className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium leading-5">{item.label}</p>
                          {item.description ? (
                            <p className="text-[11px] leading-4 text-amber-900/70 dark:text-amber-100/70">
                              {item.description}
                            </p>
                          ) : null}
                        </div>
                        {pastDueCodes.has(item.code) ? (
                          <Badge
                            variant="destructive"
                            className="mt-0.5 shrink-0 bg-red-600 px-1.5 py-0 text-[10px] text-white hover:bg-red-600"
                          >
                            Past due
                          </Badge>
                        ) : (
                          <Badge
                            variant="secondary"
                            className="mt-0.5 shrink-0 bg-amber-200 px-1.5 py-0 text-[10px] text-amber-900 hover:bg-amber-200 dark:bg-amber-800/60 dark:text-amber-100"
                          >
                            Action needed
                          </Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>

          {requirements.pendingVerification.length > 0 ? (
            <p className="mt-3 flex items-center gap-1.5 text-[11px] text-amber-900/70 dark:text-amber-100/70">
              <Clock className="size-3" />
              Stripe is reviewing {requirements.pendingVerification.length}{' '}
              {requirements.pendingVerification.length === 1
                ? 'additional item'
                : 'additional items'}
              . No action needed from you on these.
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
